import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Connection Status Banner ─────────────────────── */
export function ConnectionBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const goOnline = () => { setOnline(true); };
    const goOffline = () => { setOnline(false); setDismissed(false); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online || dismissed) return null;

  return (
    <div data-testid="connection-banner" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500,
      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      color: '#fff', padding: '8px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: '0.85rem', fontWeight: 500,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <line x1="1" y1="1" x2="23" y2="23"/>
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
        </svg>
        You're offline. Some features may be unavailable.
      </span>
      <button onClick={() => setDismissed(true)} style={{
        background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
        padding: '2px 6px', fontSize: '1rem',
      }} aria-label="Dismiss">&times;</button>
    </div>
  );
}

/* ── Notification Bell (DB-backed) ────────────────── */
export function NotificationBell({ userId, onViewAll }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/inapp-notifications/${userId}?limit=10`);
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.warn('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (notificationId) => {
    try {
      await fetch(`${API}/api/inapp-notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      setNotifications(prev => prev.map(n =>
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/inapp-notifications/${userId}/read-all`, { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const typeIcon = (type) => {
    switch (type) {
      case 'price_drop': return { color: '#10b981', label: 'Price Drop' };
      case 'booking_confirmation': return { color: '#6366f1', label: 'Booking' };
      case 'system': return { color: '#ef4444', label: 'System' };
      case 'promo': return { color: '#f59e0b', label: 'Promo' };
      default: return { color: '#94a3b8', label: 'Info' };
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        data-testid="notification-bell"
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        style={{
          background: 'none', border: 'none', color: 'var(--text-secondary, #e2e8f0)',
          cursor: 'pointer', padding: '6px', position: 'relative',
        }}
        aria-label="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span data-testid="notification-badge" style={{
            position: 'absolute', top: 0, right: 0,
            background: 'var(--accent-red, #ef4444)', color: '#fff',
            borderRadius: '50%', width: '16px', height: '16px',
            fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700,
          }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" style={{
          position: 'absolute', top: '100%', right: 0, width: '320px',
          background: 'var(--bg-card, #1e293b)', borderRadius: 'var(--radius-md, 10px)',
          boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.4))',
          border: '1px solid var(--border-light, rgba(255,255,255,0.1))',
          zIndex: 300, overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary, #f8fafc)' }}>
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </span>
            {unreadCount > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAllRead} style={{
                background: 'none', border: 'none', color: 'var(--gold-primary, #D4AF37)',
                fontSize: '0.75rem', cursor: 'pointer', fontWeight: 500,
              }}>Mark all read</button>
            )}
          </div>

          {/* Notifications list */}
          <div style={{ overflowY: 'auto', maxHeight: '340px' }}>
            {loading && notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-dim, #64748b)', fontSize: '0.85rem' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-dim, #64748b)', fontSize: '0.85rem' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const ti = typeIcon(n.type);
                return (
                  <div
                    key={n.notification_id}
                    data-testid={`notif-item-${n.notification_id}`}
                    onClick={() => !n.read && markRead(n.notification_id)}
                    style={{
                      padding: '10px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
                      background: n.read ? 'transparent' : 'rgba(212,175,55,0.04)',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: n.read ? 'transparent' : ti.color,
                        flexShrink: 0,
                      }} />
                      <span style={{
                        fontWeight: 600, fontSize: '0.8rem', color: ti.color,
                      }}>{n.title}</span>
                      <span style={{
                        marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--text-dim, #64748b)',
                      }}>{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)', paddingLeft: '14px' }}>
                      {n.message}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer - View all */}
          <div style={{
            padding: '10px 16px', borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.06))',
            textAlign: 'center',
          }}>
            <button
              data-testid="view-all-notifications-btn"
              onClick={() => { setOpen(false); onViewAll && onViewAll(); }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--gold-primary, #D4AF37)', fontSize: '0.8rem',
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
